/** @jsxImportSource @emotion/react */

import { Fragment, useEffect, useRef, useState } from 'react'
import css from '@emotion/css/macro'
import styled from '@emotion/styled/macro'
import isPropValid from '@emotion/is-prop-valid'
import { Link, Route } from 'react-router-dom'
import PropTypes from 'prop-types'
import { getDayjsTZDate } from '@sportninja/common/utils/utils'
import throttle from 'lodash.throttle'
import { t } from '@sportninja/common/i18n'
import { ROUTES } from '@sportninja/common/constants/app'
import { Helmet } from 'react-helmet'
import colors from '@sportninja/common/constants/appColors'

import {
  flairColorLight,
  font,
  MENU_HEIGHT,
  zIndex,
} from '../../components/css'
import { Container, Column, Flex } from '../../components/Layout'
import { ActionsWrapper, FixedTitle } from '../../components/PageTitle/css'
import { Desktop, media } from '../../components/Responsive'
import GamePageHeaderMatchup from './GamePageHeaderMatchup'
import MobileMiniMatchup from './MobileMiniMatchup'
import Icon from '../../components/Icon'

const MatchDateTime = styled.div`
  ${font.body}
  margin-top: 8px;
  margin-bottom: ${(props) => (props.isDesktop ? 16 : 12)}px;
  font-size: ${(props) => (props.isDesktop ? 14 : 12)}px;
  letter-spacing: ${(props) => (props.isDesktop ? 1.45 : 0.71)}px;

  ${(props) =>
    props.isDesktop
      ? css`
          padding-left: 2px;
          font-weight: bold;
          text-transform: uppercase;
        `
      : css`
          text-align: center;
        `}
`

const ScheduleLink = styled('div', {
  shouldForwardProp: (prop) => isPropValid(prop),
})`
  ${font.body}
  font-weight: bold;
  text-transform: uppercase;
  line-height: 1;

  font-size: ${(props) => (props.isDesktop ? 14 : 12)}px;
  color: ${flairColorLight};
  letter-spacing: ${(props) => (props.isDesktop ? 1.45 : 0.71)}px;
`

const GamePageHeader = ({
  actions: _actions,
  error,
  fullGameType,
  game,
  inFrame,
  organization,
  disabled,
  isSoccer = false,
}) => {
  const headerRef = useRef(null)
  const [titleVisible, setTitleVisible] = useState(false)
  const HEIGHT = !game.permission?.update ? 224 : 322

  const actions = inFrame
    ? _actions && _actions.filter((action) => action && action.key === 'list')
    : _actions

  useEffect(() => {
    const listener = throttle(() => {
      const header = headerRef.current

      if (header) {
        const currScrollPos =
          window.pageYOffset ||
          document.documentElement.scrollTop ||
          document.body.scrollTop
        let value = 1,
          threshold = HEIGHT - MENU_HEIGHT

        if (currScrollPos >= threshold) {
          value = 0
        } else if (currScrollPos > 0) {
          value = Math.min(
            1,
            Math.max(0, threshold - currScrollPos) / (1 * currScrollPos)
          )
        }

        header.style = `opacity: ${value}`

        // Also manage the visibility of the fixed title, because we broke the way
        // it normally works in PageHeader
        if (currScrollPos >= threshold) {
          setTitleVisible(true)
        } else {
          setTitleVisible(false)
        }
      }
    }, 10)

    document.addEventListener('scroll', listener)

    return () => {
      document.removeEventListener('scroll', listener)
    }
  }, [])

  let imageName = 'upcoming'
  if (game.completed) imageName = 'completed'
  else if (game.started) imageName = 'in-progress'

  const privateEntityError =
    (error && error.status === 403) || (error && error.status !== 404)

  let title = game.gameNumber
    ? `${t('util:capitalize', { text: t('common:game') })} #${game.gameNumber}`
    : game.gameNumber === null || game.gameNumber?.length === 0
    ? t('util:capitalize', { text: t('common:game') })
    : `${t('common:loading')}...`
  if (error && privateEntityError) {
    title = t('Web:private')
  }

  return (
    <Desktop>
      {(isDesktop) => {
        let imageUrl = ''
        if (!inFrame) {
          if (title !== `${t('common:loading')}...`) {
            if (isSoccer) {
              imageUrl = '/images/soccer_game_background'
            } else {
              imageUrl = `/images/game_${imageName}${
                isDesktop ? '' : '_mobile'
              }`
            }
          }
        }
        return (
          <Container
            css={css`
              min-height: ${isDesktop ? 282 : HEIGHT}px;
              background: no-repeat center center / cover url('${imageUrl}.png');

              ${isDesktop
                ? css`
                    margin-bottom: 16px;
                  `
                : css`
                    position: relative;
                    padding: 0 0 ${HEIGHT}px;
                  `}
            `}
            wrapCss={css`
              position: relative;
            `}
          >
            <Helmet title={title} />
            {!isDesktop && (
              <FixedTitle noFlex justifyContent='center' visible={titleVisible}>
                <div>
                  <h2>{title}</h2>
                </div>
              </FixedTitle>
            )}

            {isDesktop && disabled && (
              <Route exact path='/game/:game/timeline'>
                <div
                  css={css`
                    position: fixed;
                    top: 0;
                    height: 50px;
                    left: 0;
                    right: 0;
                    z-index: ${zIndex.header + 1};
                    background-color: rgba(0, 0, 0, 0.77);
                    cursor: not-allowed;
                  `}
                ></div>

                <Flex
                  column
                  alignItems='center'
                  justifyContent='center'
                  className='disabled-overlay'
                  css={css`
                    position: absolute;
                    top: -50px;
                    left: -75%;
                    right: -75%;
                    bottom: -48px;
                    z-index: ${zIndex.header - 1};
                    background-color: rgba(0, 0, 0, 0.77);
                    cursor: not-allowed;
                    font-size: 20px;
                    color: ${colors.DEFAULT_FLAIR};

                    ${media.ten} {
                      bottom: -64px;
                    }
                  `}
                >
                  <Icon
                    name='exclamation-triangle'
                    fontSize={40}
                    css={css`
                      margin-bottom: 20px;
                    `}
                    color={colors.DEFAULT_FLAIR}
                  />
                  Please do not leave the page while editing the game timeline.
                  <p
                    css={css`
                      margin-top: 4px;
                    `}
                  >
                    Press the &apos;Save Timeline&apos; button to exit.
                  </p>
                </Flex>
              </Route>
            )}
            <div
              ref={headerRef}
              css={css`
                width: 100%;

                ${!isDesktop &&
                css`
                  position: fixed;
                  padding: 0 8px 16px;
                `};
              `}
            >
              <Column column fullWidth>
                <div
                  css={css`
                    margin-bottom: 8px;
                    margin-top: ${isDesktop ? 20 : 16}px;

                    ${isDesktop
                      ? css`
                          min-height: 14px;
                        `
                      : css`
                          text-align: center;
                        `}
                  `}
                >
                  {game.rootSchedule && (
                    <>
                      <ScheduleLink
                        as={Link}
                        isDesktop={isDesktop}
                        to={`${ROUTES.ORG_ROOT}/${organization?.id}`}
                      >
                        {organization && organization?.name ? (
                          <Fragment>{organization.name}</Fragment>
                        ) : null}
                      </ScheduleLink>
                      {' > '}
                      <ScheduleLink
                        as={Link}
                        isDesktop={isDesktop}
                        to={`${ROUTES.SCHEDULE_ROOT}/${game.rootSchedule.id}`}
                      >
                        {game.rootSchedule.name_full || game.rootSchedule.name}
                      </ScheduleLink>
                      {game.parentId !== game?.rootSchedule?.id && (
                        <>
                          {' > '}
                          <ScheduleLink
                            as={Link}
                            isDesktop={isDesktop}
                            to={`${ROUTES.SCHEDULE_ROOT}/${game.rootSchedule.id}?scheduleType=${game.parentScheduleType}&schedule_id=${game.parentId}`}
                          >
                            {game.parentSchedule}
                          </ScheduleLink>
                        </>
                      )}
                    </>
                  )}
                </div>
                <Flex>
                  <div
                    css={css`
                      flex: 1;
                      ${font.title}
                      font-weight: bold;
                      line-height: 1;

                      font-size: ${isDesktop ? 44 : 28}px;
                      letter-spacing: ${isDesktop ? 0.64 : 0.41}px;

                      ${!isDesktop &&
                      css`
                        text-align: center;
                      `}
                    `}
                  >
                    {title}
                  </div>
                  {isDesktop && actions && (
                    <ActionsWrapper>{actions}</ActionsWrapper>
                  )}
                </Flex>
                {game.startsAt && (
                  <MatchDateTime isDesktop={isDesktop}>
                    {getDayjsTZDate(
                      game.startsAt,
                      game?.location?.timezone
                    ).format(
                      isDesktop
                        ? 'h:mm A z - dddd MMM DD/YYYY'
                        : 'h:mm A z - MMM DD, YYYY'
                    )}
                    {game.startsAt && fullGameType && (
                      <span> • {fullGameType.name}</span>
                    )}
                  </MatchDateTime>
                )}
              </Column>

              {!error && (
                <Column column fullWidth>
                  <GamePageHeaderMatchup
                    isDesktop={isDesktop}
                    game={game}
                    isSoccer={isSoccer}
                  />
                </Column>
              )}

              {!isDesktop && actions && (
                <Flex justifyContent='center'>
                  <ActionsWrapper>{actions}</ActionsWrapper>
                </Flex>
              )}
            </div>
            {!isDesktop && (
              <MobileMiniMatchup game={game} isSticky={titleVisible} />
            )}
          </Container>
        )
      }}
    </Desktop>
  )
}

GamePageHeader.propTypes = {
  actions: PropTypes.node,
  game: PropTypes.oneOfType([PropTypes.bool, PropTypes.object]).isRequired,
}

export default GamePageHeader
